import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Phone, MessageCircle, Mail, PhoneCall, CheckCircle2, XCircle, Skull } from "lucide-react";

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return d; } }

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [lostOpen, setLostOpen] = useState(false);
  const [deadOpen, setDeadOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostOther, setLostOther] = useState("");
  const [deadReason, setDeadReason] = useState("");
  const [convertTarget, setConvertTarget] = useState("IMMIGRATION");
  const [convertOfficer, setConvertOfficer] = useState("");
  const [convertAppType, setConvertAppType] = useState("");

  // Constrain retain target by lead department; user must reclassify the lead first if wrong.
  useEffect(() => {
    if (!data?.lead) return;
    const d = data.lead.department;
    if (d === "STUDY") setConvertTarget("B2C");
    else if (d === "FILING") setConvertTarget("IMMIGRATION");
  }, [data?.lead?.department]); // eslint-disable-line

  const load = async () => {
    const [d, m] = await Promise.all([api.get(`/leads/${id}`), api.get("/leads/meta")]);
    setData(d.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!data || !meta) return <div className="text-slate-500">Loading…</div>;

  const l = data.lead;

  const patch = async (body) => { try { await api.patch(`/leads/${id}`, body); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const submitAttempt = async () => {
    if (!attempt?.method || !attempt?.outcome) { toast.error("Method and outcome required"); return; }
    try { await api.post(`/leads/${id}/contact-attempts`, attempt); toast.success("Contact recorded"); setAttempt(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitLost = async () => {
    if (!lostReason) { toast.error("Pick a reason"); return; }
    if (lostReason === "Other" && !lostOther.trim()) { toast.error("Explanation required"); return; }
    try { await patch({ stage: "Lost", lost_reason: lostReason === "Other" ? `Other: ${lostOther.trim()}` : lostReason }); setLostOpen(false); }
    catch(_){ /* patch handles toast */ }
  };
  const submitDead = async () => {
    if (!deadReason) { toast.error("Pick a reason"); return; }
    try { await patch({ stage: "Dead / Unresponsive", dead_reason: deadReason }); setDeadOpen(false); } catch(_){}
  };
  const submitConvert = async () => {
    if (convertTarget === "IMMIGRATION" && !convertAppType) {
      toast.error("Pick the Application Type for this file");
      return;
    }
    try {
      const r = await api.post(`/leads/${id}/convert`, {
        target: convertTarget,
        assigned_officer_id: convertOfficer || null,
        app_type_id: convertTarget === "IMMIGRATION" ? (convertAppType || null) : null,
      });
      toast.success(`Retained → ${r.data.application.code}`);
      setConvertOpen(false); load();
      // Deep-link to the created file
      if (r.data.application.module === "IMMIGRATION") nav(`/immigration/applications/${r.data.application.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const wa = `https://wa.me/${(l.phone||"").replace(/[^0-9+]/g,"")}`;

  return (
    <div className="space-y-6">
      <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4"/>Back to leads</Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="heading-font text-2xl font-bold text-slate-900">{l.full_name}</h1>
              <span className="mono text-xs text-slate-500">{l.lead_id}</span>
              <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">{l.source}</span>
            </div>
            <div className="text-sm text-slate-600 mt-1">{l.phone} · {l.email || "no email"} · {l.app_type_name || "—"}</div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span data-testid="lead-department-badge" className={`stage-badge ${l.department==="FILING"?"bg-indigo-50 text-indigo-800 border-indigo-200":"bg-teal-50 text-teal-800 border-teal-200"}`}>
                {l.department === "FILING" ? "Filing Team" : "Study Team"}
              </span>
              <span className={`stage-badge ${l.stage==="Retained"?"bg-emerald-50 text-emerald-800 border-emerald-200":l.stage==="Lost"?"bg-rose-50 text-rose-800 border-rose-200":"bg-slate-50 text-slate-700 border-slate-200"}`}>{l.stage}</span>
              {l.is_converted && l.converted_app_code && <Link to={l.converted_target==="IMMIGRATION"?`/immigration/applications/${l.converted_app_id}`:"#"} className="stage-badge bg-teal-50 text-teal-800 border-teal-200">→ {l.converted_app_code}</Link>}
              {l.caller_name ? <span className="stage-badge bg-blue-50 text-blue-800 border-blue-200">Caller: {l.caller_name}</span> : <span className="stage-badge bg-rose-50 text-rose-800 border-rose-200">Unassigned</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a title="Call" href={`tel:${l.phone}`} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Phone className="w-3 h-3"/>Call</a>
            <a title="WhatsApp" href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><MessageCircle className="w-3 h-3"/>WhatsApp</a>
            {l.email && <a title="Email" href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Mail className="w-3 h-3"/>Email</a>}
            {data.permissions.edit && !l.is_converted && <>
              <Button data-testid="lead-contact-btn" size="sm" onClick={()=>setAttempt({ method: "Phone Call", outcome: "" })}><PhoneCall className="w-4 h-4 mr-1"/>Log Contact</Button>
              <Button data-testid="lead-retain-btn" size="sm" onClick={()=>{ setConvertOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1"/>Retain</Button>
              <Button data-testid="lead-lost-btn" size="sm" variant="outline" onClick={()=>setLostOpen(true)} className="text-rose-600 border-rose-200"><XCircle className="w-4 h-4 mr-1"/>Lost</Button>
              <Button data-testid="lead-dead-btn" size="sm" variant="outline" onClick={()=>setDeadOpen(true)}><Skull className="w-4 h-4 mr-1"/>Dead</Button>
            </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {data.permissions.edit && !l.is_converted && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <div className="heading-font font-semibold">Update</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Stage">
                  <Select value={l.stage} onValueChange={v=>patch({stage: v})}>
                    <SelectTrigger data-testid="lead-stage-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{meta.active_stages.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Next Follow-Up"><Input data-testid="lead-followup-input" type="datetime-local" defaultValue={(l.next_followup_at||"").slice(0,16)} onBlur={e=>patch({next_followup_at: e.target.value})}/></F>
                <F label="Assigned Caller">
                  <Select value={l.assigned_caller_id||""} onValueChange={v=>patch({assigned_caller_id: v})}>
                    <SelectTrigger data-testid="lead-caller-select"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                    <SelectContent>{meta.callers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Filing Officer">
                  <Select value={l.assigned_filing_officer_id||""} onValueChange={v=>patch({assigned_filing_officer_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Not assigned"/></SelectTrigger>
                    <SelectContent>{meta.filing_officers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Department (Team)">
                  <Select value={l.department||""} onValueChange={v=>patch({department: v})}>
                    <SelectTrigger data-testid="lead-department-select"><SelectValue placeholder="Pick team"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STUDY">Study (Admissions)</SelectItem>
                      <SelectItem value="FILING">Filing (Immigration)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Move this lead to the other team if it was misclassified. Convert target updates automatically.</p>
                </F>
                <div className="sm:col-span-2"><F label="Notes"><Textarea defaultValue={l.notes||""} onBlur={e=>e.target.value !== l.notes && patch({notes: e.target.value})}/></F></div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold">Contact Timeline ({data.contacts.length})</div>
            <div className="divide-y divide-slate-100">
              {data.contacts.length===0 && <div className="p-5 text-sm text-slate-500">No contact attempts yet. Log one with the button above.</div>}
              {data.contacts.map(c => (
                <div key={c.id} className="p-4 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{c.actor_name}</span>
                    <span className="stage-badge bg-blue-50 text-blue-800 border-blue-200">{c.method}</span>
                    <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">{c.outcome}</span>
                    <span className="text-xs text-slate-400 ml-auto">{fmt(c.attempted_at)}</span>
                  </div>
                  {c.notes && <div className="mt-1 text-slate-700 whitespace-pre-wrap">{c.notes}</div>}
                  {c.next_followup_at && <div className="text-xs text-amber-700 mt-1">Next follow-up: {fmt(c.next_followup_at)}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2 text-sm">
            <Row label="Received" v={fmt(l.received_at)}/>
            <Row label="Created By" v={l.creator_name}/>
            <Row label="Last Updated" v={fmt(l.updated_at)}/>
            <Row label="Last Contacted" v={fmt(l.last_contacted_at)}/>
            <Row label="Age" v={l.age_days!=null?`${l.age_days} day(s)`:"—"}/>
            {l.lost_reason && <Row label="Lost Reason" v={l.lost_reason} accent="text-rose-700"/>}
            {l.dead_reason && <Row label="Dead Reason" v={l.dead_reason} accent="text-slate-700"/>}
          </div>
        </div>
      </div>

      {/* Log contact dialog */}
      <Dialog open={!!attempt} onOpenChange={o=>!o && setAttempt(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Log Contact Attempt</DialogTitle></DialogHeader>
          {attempt && <div className="space-y-3">
            <F label="Method *">
              <Select value={attempt.method} onValueChange={v=>setAttempt({...attempt, method:v})}>
                <SelectTrigger data-testid="lead-attempt-method"><SelectValue/></SelectTrigger>
                <SelectContent>{meta.contact_methods.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Outcome *">
              <Select value={attempt.outcome||""} onValueChange={v=>setAttempt({...attempt, outcome:v})}>
                <SelectTrigger data-testid="lead-attempt-outcome"><SelectValue placeholder="Pick outcome"/></SelectTrigger>
                <SelectContent>{meta.contact_outcomes.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Attempted At"><Input type="datetime-local" value={(attempt.attempted_at||"").slice(0,16)} onChange={e=>setAttempt({...attempt, attempted_at: e.target.value})}/></F>
            <F label="Next Follow-Up"><Input type="datetime-local" value={(attempt.next_followup_at||"").slice(0,16)} onChange={e=>setAttempt({...attempt, next_followup_at: e.target.value})}/></F>
            <F label="Move Stage To (optional)">
              <Select value={attempt.new_stage||""} onValueChange={v=>setAttempt({...attempt, new_stage:v})}>
                <SelectTrigger><SelectValue placeholder="Keep current"/></SelectTrigger>
                <SelectContent>{meta.active_stages.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Notes"><Textarea value={attempt.notes||""} onChange={e=>setAttempt({...attempt, notes:e.target.value})}/></F>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAttempt(null)}>Cancel</Button>
            <Button data-testid="lead-attempt-save" onClick={submitAttempt} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lost dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Mark Lead as Lost</DialogTitle></DialogHeader>
          <F label="Reason *">
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="lead-lost-reason"><SelectValue placeholder="Pick reason"/></SelectTrigger>
              <SelectContent>{meta.lost_reasons.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          {lostReason === "Other" && <F label="Explanation *"><Textarea value={lostOther} onChange={e=>setLostOther(e.target.value)}/></F>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setLostOpen(false)}>Cancel</Button>
            <Button data-testid="lead-lost-save" onClick={submitLost} className="bg-rose-600 hover:bg-rose-700 text-white">Mark Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dead dialog */}
      <Dialog open={deadOpen} onOpenChange={setDeadOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Mark Lead as Dead / Unresponsive</DialogTitle></DialogHeader>
          <F label="Reason *">
            <Select value={deadReason} onValueChange={setDeadReason}>
              <SelectTrigger data-testid="lead-dead-reason"><SelectValue placeholder="Pick reason"/></SelectTrigger>
              <SelectContent>{meta.dead_reasons.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDeadOpen(false)}>Cancel</Button>
            <Button data-testid="lead-dead-save" onClick={submitDead} className="bg-slate-800 hover:bg-slate-900 text-white">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert / Retain dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Retain Lead — Create Client File</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className={`text-xs rounded-md p-2 border ${l.department==="FILING"?"bg-indigo-50 border-indigo-200 text-indigo-900":"bg-teal-50 border-teal-200 text-teal-900"}`}>
              {l.department === "FILING"
                ? "This is a Filing lead — it will convert to an Immigration file."
                : "This is a Study lead — it will convert to a B2C admissions file."}
              {" "}Wrong team? Close this dialog, change the department in the Update card, then retain.
            </div>
            <F label="Target module (locked by department)">
              <Select value={convertTarget} disabled>
                <SelectTrigger data-testid="lead-convert-target"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="IMMIGRATION">Immigration</SelectItem>
                  <SelectItem value="B2C">B2C Admissions</SelectItem>
                  <SelectItem value="B2B">B2B Admissions</SelectItem>
                </SelectContent>
              </Select>
            </F>
            {convertTarget === "IMMIGRATION" && (
              <>
                <F label="Application Type (required)">
                  <Select value={convertAppType} onValueChange={setConvertAppType}>
                    <SelectTrigger data-testid="lead-convert-app-type"><SelectValue placeholder="Select the actual application type"/></SelectTrigger>
                    <SelectContent>{meta.app_types.map(t=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {l.app_type_name && (
                    <p className="text-[11px] text-slate-500 mt-1">Lead inquiry topic: <span className="font-medium text-slate-700">{l.app_type_name}</span> (not carried over — pick the correct type manually)</p>
                  )}
                </F>
                <F label="Assign Filing Officer (optional)">
                  <Select value={convertOfficer} onValueChange={setConvertOfficer}>
                    <SelectTrigger data-testid="lead-convert-officer"><SelectValue placeholder="Filing Manager can assign later"/></SelectTrigger>
                    <SelectContent>{meta.filing_officers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </>
            )}
            <p className="text-xs text-slate-500">Notes, source, and activity history will transfer automatically. The lead record is preserved and marked Retained.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setConvertOpen(false)}>Cancel</Button>
            <Button data-testid="lead-convert-save" onClick={submitConvert} className="bg-emerald-600 hover:bg-emerald-700 text-white">Create & Retain</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>);
const Row = ({ label, v, accent }) => (<div className="flex items-center justify-between text-sm"><span className="text-slate-500">{label}</span><span className={`font-semibold ${accent||"text-slate-900"}`}>{v || "—"}</span></div>);
